import { useState, useEffect } from 'react';
import { platform } from '@/lib/platform';

export function InstallPrompt({ visible = true }: { visible?: boolean } = {}) {
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    if (platform.isNative) return;

    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) return;

    // Check if dismissed recently (7 day cooldown)
    const dismissed = localStorage.getItem('install-prompt-dismissed');
    if (dismissed && Date.now() - Number(dismissed) < 7 * 24 * 60 * 60 * 1000) return;

    // iOS detection
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent);
    setIsIOS(isIOSDevice);
    if (isIOSDevice) {
      setShowPrompt(true);
      return;
    }

    // Android/Chrome: listen for beforeinstallprompt
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowPrompt(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  // Don't show install prompt inside native app
  if (platform.isNative) return null;

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      await deferredPrompt.userChoice;
    }
    handleDismiss();
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('install-prompt-dismissed', String(Date.now()));
  };

  // Keep the one-shot browser event and its listener across task-mode transitions.
  if (!showPrompt || !visible) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 z-[9999] bg-white dark:bg-popover rounded-xl shadow-2xl border p-4 flex items-center gap-3 max-w-md mx-auto">
      <img src="/icons/icon-192.png" alt="Forge" className="w-12 h-12 rounded-xl" />
      <div className="flex-1">
        <p className="font-semibold text-sm">Install Forge</p>
        <p className="text-xs text-muted-foreground">
          {isIOS ? 'Tap Share then "Add to Home Screen"' : 'Add to your home screen for quick access'}
        </p>
      </div>
      {!isIOS && (
        <button onClick={handleInstall} className="bg-primary text-primary-foreground text-sm px-3 py-1.5 rounded-lg font-medium">
          Install
        </button>
      )}
      <button onClick={handleDismiss} className="text-muted-foreground text-xs" aria-label="Dismiss install prompt">
        ✕
      </button>
    </div>
  );
}
